<template>
  <onboarding-container include-back-button @goBack="goBack">
    <div class="mt-200">
      <h3 class="onboarding--subheading">Which Services would you like to set up:</h3>

      <div class="mt-100 flex flex-wrap flex-between">
        <template v-if="hasPhoneFeature">
          <onboarding-integration-box
            type="Phone System"
            heading="smrtPhone"
            icon-path="/images/smrtphone-icon.png"
            :is-included="chosenIntegrations.smrtPhone.included"
            @changeStatus="changeStatus('smrtPhone')"
          >
            <p>smrtPhone is an all-in-one communication platform for sales-driven businesses.</p>
          </onboarding-integration-box>

          <onboarding-integration-box
            type="Phone System"
            heading="CallRail"
            icon-path="/images/callrail-icon.png"
            :is-included="chosenIntegrations.callRail.included"
            @changeStatus="changeStatus('callRail')"
          >
            <p>CallRail helps you track phone calls and gives you analytics that help you optimize your marketing.</p>
          </onboarding-integration-box>
        </template>

        <template v-if="hasEmailFeature">
          <onboarding-integration-box
            type="Email System"
            heading="Gmail"
            icon-path="/images/gmail-icon.png"
            :is-included="chosenIntegrations.gmail.included"
            @changeStatus="changeStatus('gmail')"
            :is-disabled="chosenIntegrations.customDomain.included"
            disabled-text="Can't have Gmail with Custom Email Domain"
          >
            <p>Private and secure email at no cost.</p>
          </onboarding-integration-box>

          <onboarding-integration-box
            type="Email System"
            heading="Custom Email Domain"
            icon-path="/images/email-icon.png"
            :is-included="chosenIntegrations.customDomain.included"
            @changeStatus="changeStatus('customDomain')"
            :is-disabled="chosenIntegrations.gmail.included"
            disabled-text="Can't have Custom Email Domain with Gmail"
          >
            <p>Use your domain to send and receive email inside Forefront.</p>
          </onboarding-integration-box>
        </template>

        <onboarding-integration-box
          type="Texting Platform"
          heading="Launch Control"
          icon-path="/images/launch-control-icon.png"
          :is-included="chosenIntegrations.launchControl.included"
          @changeStatus="changeStatus('launchControl')"
        >
          <p>Launch Control is text messaging for real estate professionals.</p>
        </onboarding-integration-box>

        <onboarding-integration-box
          v-if="hasCalendarFeature"
          type="Calendar"
          heading="Google Calendar"
          icon-path="/images/google-calendar-icon.png"
          :is-included="chosenIntegrations.googleCalendar.included"
          @changeStatus="changeStatus('googleCalendar')"
        >
          <p>Google Calendar allows you to spend less time planning and more time doing.</p>
        </onboarding-integration-box>
      </div>
    </div>

    <div class="my-200 flex flex-between">
      <button-outlined
        @click="goToAccount"
        size=""
      >
        Nevermind, Go To Account
      </button-outlined>

      <button-default
        @click="goToIntegrationStep"
        size=""
        :disabled="disableIntegrationSetup"
      >Continue to Integration Setup
      </button-default>
    </div>
  </onboarding-container>
</template>

<script>
import {defineComponent} from 'vue'
import OnboardingContainer from "./component/OnboardingContainer.vue";
import OnboardingIntegrationBox from "./component/OnboardingIntegrationBox.vue";
import IntegrationBox from "../settings/components/IntegrationBox.vue";
import api from "../../api";
import {SnackbarProgrammatic as Snackbar} from "buefy";

export default defineComponent({
  name: "OnboardingWizard2Page",
  metaInfo: {
    title: "Choose Integrations"
  },
  components: {IntegrationBox, OnboardingIntegrationBox, OnboardingContainer},
  data() {
    return {
      chosenIntegrations: {
        smrtPhone: {
          included: false,
          completed: false,
        },
        callRail: {
          included: false,
          completed: false,
        },
        gmail: {
          included: false,
          completed: false,
        },
        customDomain: {
          included: false,
          completed: false,
        },
        launchControl: {
          included: false,
          completed: false,
        },
        googleCalendar: {
          included: false,
          completed: false,
        },
      },
    }
  },
  computed: {
    onboarding() {
      return this.$store.state.onboarding.onboarding;
    },
    hasPhoneFeature() {
      return this.$store.getters.account.has_phone_integration_feature
    },
    hasEmailFeature() {
      return this.$store.getters.account.has_email_integration_feature
    },
    hasCalendarFeature() {
      return this.$store.getters.account.has_calendar_integration_feature
    },
    disableIntegrationSetup() {
      let disable = false;
      const keys = Object.keys(this.chosenIntegrations)

      keys.forEach(key => {
        if (this.chosenIntegrations[key].included === false) {
          disable = true;
        }
      })

      keys.forEach(key => {
        if (this.chosenIntegrations[key].included) {
          disable = false;
        }
      })

      return disable;
    }
  },
  methods: {
    changeStatus(key) {
      this.chosenIntegrations[key].included = !this.chosenIntegrations[key].included;
    },
    goToIntegrationStep() {
      api.onboarding.updateChosenIntegration(this.onboarding.id, this.chosenIntegrations).then((response) => {
        this.$store.commit('onboarding/updateOnboarding', response.data);
        this.$router.push({name: 'onboarding-wizard-3'})
      })
    },
    goBack() {
      this.$router.push({name: 'onboarding-provisioning'})
    },
    goToAccount() {
      api.onboarding.markComplete(this.onboarding.id).then((response) => {
        this.$store.commit('onboarding/updateOnboarding', response.data);
        this.$router.push({name: 'pipeline'})
      }).catch(() => {
        Snackbar.open({
          message: 'Your account was not provisioned. Please contact support@forefrontcrm.com so we can get this sorted out.',
          type: 'is-danger',
          position: 'is-bottom',
          indefinite: true
        });
      })
    },
  },
})
</script>

<style scoped lang="scss">
.onboarding {
  &--heading {
    margin: 1rem 0;
    font-size: 2.5rem;
    font-weight: bold;
    text-align: center;
  }

  &--content {
    margin-top: 2rem;
  }

  &--text {
    margin-bottom: 10px;
    line-height: 1.3;
  }

  &--subheading {
    margin-bottom: 1rem;
    font-size: 1.25rem;
    font-weight: bold;
  }
}
</style>
